import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, Star, X, ChevronDown, Loader2 } from 'lucide-react'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useOutsideClick } from '@/hooks/useOutsideClick'
import BottomSheet from '@/components/Common/BottomSheet'
import { sourceForAssetType } from '@/utils/symbolSource'

const ASSET_CATEGORIES = [
  { key: 'crypto', label: 'Crypto', source: 'bingx' },
  { key: 'forex',  label: 'Forex',  source: 'twelve' },
  { key: 'indices', label: 'Índices', source: 'twelve' },
  { key: 'stocks',  label: 'Acciones', source: 'twelve' },
  { key: 'commodities', label: 'Commodities', source: 'twelve' },
]

const POPULAR_SYMBOLS = {
  crypto: [
    'BTC/USDT:USDT', 'ETH/USDT:USDT', 'SOL/USDT:USDT', 'XRP/USDT:USDT',
    'DOGE/USDT:USDT', 'ADA/USDT:USDT', 'AVAX/USDT:USDT', 'LINK/USDT:USDT',
  ],
  forex: [
    'EUR/USD', 'GBP/USD', 'USD/JPY', 'USD/CHF',
    'AUD/USD', 'USD/CAD', 'EUR/GBP', 'EUR/JPY',
  ],
  indices: [
    'SPX', 'NDX', 'DJI', 'RUT',
    'FTSE', 'DAX', 'CAC', 'N225',
  ],
  stocks: [
    'AAPL', 'MSFT', 'GOOGL', 'AMZN',
    'TSLA', 'NVDA', 'META', 'NFLX',
  ],
  commodities: [
    'XAU/USD', 'XAG/USD', 'WTI', 'BRENT',
    'NATGAS', 'COPPER', 'WHEAT', 'CORN',
  ],
}

const SEARCH_TIMEOUT_MS = 15000

// `fetchCryptoMarkets`/`searchSymbols` se inyectan por props (no se importa
// ningún servicio aquí) para que este componente sea reutilizable tal cual
// desde el chart público sin arrastrar los servicios privados con JWT
// (`chartingService`/`exchangeAccountsService`, ambos sobre `services/api.js`
// con su interceptor 401->/login) al bundle público. ChartPage.jsx (privado)
// pasa esos mismos servicios; ChartPublicPage.jsx pasa los equivalentes
// contra /api/public/*.
export default function SymbolSelector({
  selectedSymbol,
  onSelect,
  favorites,
  onToggleFavorite,
  fetchCryptoMarkets,
  searchSymbols,
  className = '',
  compact = false,
  trigger,
  buttonClassName = '',
  menuClassName = '',
}) {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('crypto')
  const [search, setSearch] = useState('')
  const [symbols, setSymbols] = useState([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef(null)
  const abortRef = useRef(null)

  const staticSymbols = POPULAR_SYMBOLS[activeTab] || []
  const cryptoCacheRef = useRef(null)

  const loadSymbols = useCallback(async () => {
    if (activeTab === 'crypto') {
      if (cryptoCacheRef.current) {
        setSymbols(cryptoCacheRef.current)
        return
      }
      setLoading(true)
      try {
        const res = await fetchCryptoMarkets('bingx')
        const list = (Array.isArray(res.data) ? res.data : []).map(s => ({
          symbol: s,
          description: s.replace('/USDT:USDT', '').replace('/USDC:USDC', ''),
          type: 'crypto',
        }))
        cryptoCacheRef.current = list
        setSymbols(list)
      } catch (e) {
        setSymbols(staticSymbols.map(s => ({ symbol: s, description: s.replace('/USDT:USDT', '').replace('/USDC:USDC', ''), type: 'crypto' })))
      } finally {
        setLoading(false)
      }
      return
    }

    if (abortRef.current) {
      abortRef.current.abort()
    }
    const controller = new AbortController()
    abortRef.current = controller

    // Para acciones, el listado completo de Twelve Data es muy grande y lento.
    // Mostramos los populares estáticos hasta que el usuario escriba al menos 2 chars.
    if (activeTab === 'stocks' && search.trim().length < 2) {
      setSymbols(staticSymbols.map(s => ({ symbol: s, description: s, type: 'stocks' })))
      return
    }

    setLoading(true)
    const timeoutId = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS)

    try {
      const res = await searchSymbols(search.trim(), 'twelve', activeTab)
      const list = Array.isArray(res.data) ? res.data : []
      setSymbols(list)
    } catch (e) {
      // Fallback a lista estática en caso de error/timeout
      setSymbols(staticSymbols.map(s => ({ symbol: s, description: s, type: activeTab })))
    } finally {
      clearTimeout(timeoutId)
      setLoading(false)
    }
  }, [activeTab, search, staticSymbols, fetchCryptoMarkets, searchSymbols])

  useEffect(() => {
    if (!isOpen) return
    const timer = setTimeout(() => loadSymbols(), 300)
    return () => {
      clearTimeout(timer)
      if (abortRef.current) {
        abortRef.current.abort()
      }
    }
  }, [isOpen, activeTab, search, loadSymbols])

  useOutsideClick(containerRef, () => setIsOpen(false))

  const handleSelect = useCallback((symbol, source) => {
    onSelect(symbol, source)
    setIsOpen(false)
    setSearch('')
  }, [onSelect])

  // La fuente de velas se deriva del `type` del item del catálogo cuando está
  // disponible (más fiable que el tab activo: la búsqueda puede mezclar
  // resultados); si el item no trae type, se usa la fuente del tab.
  const activeTabSource = ASSET_CATEGORIES.find(c => c.key === activeTab)?.source

  // Línea secundaria de cada fila: descripción (nombre del activo) + mercado
  // (exchange) + divisa. Los códigos crudos de Twelve Data son ambiguos sin
  // esto ('000001' es una acción de Shenzhen, no el índice de Shanghái).
  function secondaryLine(s) {
    if (!s.type || s.type === 'crypto') return s.description
    return [s.description, s.exchange, s.currency_code].filter(Boolean).join(' · ')
  }

  const filtered = search.trim()
    ? symbols.filter(s => s.symbol?.toUpperCase().includes(search.toUpperCase()))
    : symbols

  const menuContent = (
    <>
      <div className="flex border-b overflow-x-auto" style={{ borderColor: 'var(--hair)', background: 'var(--green-panel-2)' }}>
        {ASSET_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            onClick={() => { setActiveTab(cat.key); setSearch('') }}
            className={`px-3 py-2.5 md:py-2 text-xs font-medium whitespace-nowrap transition-colors ${
              activeTab === cat.key
                ? 'text-[var(--green-deep)]'
                : 'text-[var(--txt-dim)] hover:bg-[var(--green-panel)]'
            }`}
            style={activeTab === cat.key ? { background: 'var(--gold)' } : undefined}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="p-2 border-b" style={{ borderColor: 'var(--hair)', background: 'var(--green-panel)' }}>
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--txt-dim)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar símbolo…"
            className="w-full pl-7 pr-7 py-2 md:py-1.5 text-sm rounded border focus:outline-none"
            style={{ background: 'var(--green-panel-2)', borderColor: 'var(--hair)', color: 'var(--ivory)' }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--txt-dim)] hover:text-[var(--ivory)]"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto" style={{ background: 'var(--green-panel)' }}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-4 text-sm" style={{ color: 'var(--txt-dim)' }}>
            <Loader2 size={14} className="animate-spin" />
            Cargando símbolos…
          </div>
        ) : filtered.length === 0 ? (
          <p className="p-3 text-sm" style={{ color: 'var(--txt-dim)' }}>No se encontraron símbolos</p>
        ) : (
          filtered.slice(0, 100).map(s => {
            const isFav = favorites.includes(s.symbol)
            return (
              <div
                key={s.symbol}
                className="flex items-center hover:bg-[var(--green-panel-2)]"
                style={s.symbol === selectedSymbol ? { background: 'rgba(176, 141, 87, 0.12)' } : undefined}
              >
                <button
                  onClick={() => handleSelect(s.symbol, s.type ? sourceForAssetType(s.type) : activeTabSource)}
                  className="flex-1 px-4 py-2.5 md:py-2 text-left text-sm"
                >
                  <div style={{ color: s.symbol === selectedSymbol ? 'var(--gold-light)' : 'var(--ivory)', fontWeight: 500 }}>
                    {s.symbol}
                  </div>
                  <div className="text-xs truncate" style={{ color: 'var(--txt-dim)' }}>{secondaryLine(s)}</div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(s.symbol) }}
                  className={`px-2.5 py-2.5 md:px-2 md:py-2 ${isFav ? 'text-amber-400' : 'text-[var(--txt-dim)] hover:text-amber-400'}`}
                >
                  <Star size={14} className={isFav ? 'fill-amber-400' : ''} />
                </button>
              </div>
            )
          })
        )}
      </div>
    </>
  )

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {trigger ? (
        <button onClick={() => setIsOpen(!isOpen)} className="block w-full text-left">
          {trigger({ isOpen, selectedSymbol })}
        </button>
      ) : (
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName || (compact
          ? 'flex items-center gap-1 px-2 py-1.5 rounded-md bg-slate-800 text-white text-xs hover:bg-slate-700 transition-colors flex-shrink-0'
          : 'flex items-center gap-2 px-3 py-2.5 md:py-1.5 rounded-md bg-slate-800 text-white text-sm hover:bg-slate-700 transition-colors'
        )}
      >
        {/* En compacto (cabecera móvil) se quita el sufijo redundante
            ":USDT"/":USDC" solo visualmente — el valor real (selectedSymbol,
            usado para la petición de datos) no cambia. */}
        <span className="font-mono">{compact ? selectedSymbol.replace(/:[A-Z]+$/, '') : selectedSymbol}</span>
        <ChevronDown size={12} />
      </button>
      )}

      {isOpen && (isMobile ? (
        <BottomSheet title="Símbolo" onClose={() => setIsOpen(false)}>
          <div style={{ background: 'var(--green-panel)' }}>{menuContent}</div>
        </BottomSheet>
      ) : (
        <div className={`absolute top-full left-0 mt-1 w-80 rounded-lg shadow-xl z-[60] overflow-hidden ${menuClassName}`} style={{ background: 'var(--green-panel)', border: '1px solid var(--hair)' }}>
          {menuContent}
        </div>
      ))}
    </div>
  )
}
